/* An HTML draft as the article it is, and edited where the words are.
 *
 * The draft is the DNN template: tc-* sections, a facts table, an FAQ with
 * schema.org markup, a review quote whose spans ARE the Review schema, the
 * photo grid withCaseStudyPhotos keeps in sync. An editor library would
 * re-serialise all of that through its own schema and drop what it does not
 * know, so this is contentEditable over the real markup, switched on in the
 * template's sections (SPOTS). Everything structural inside them (LOCKED) is an
 * island that cannot be typed into or deleted, and the photo blocks show as
 * placeholders whose HTML is put back untouched on save. Anything else stays
 * editable in the HTML tab.
 *
 * Paste is plain text, as in RichTextEditor, so formatting copied from Word or
 * a website does not arrive as spans and styles DNN would mangle. */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/lib/icons';
import { normalizeLink } from '@/components/RichTextEditor';
import { Modal } from '@/components/studio/Modal';
import { Button } from '@/components/ui';

/** The editable sections of the house template. */
const SPOTS = '.tc-lede, .tc-takeaways, .tc-context, .tc-facts, .tc-content, .tc-cta__inner, .tc-faq';
/** Structure inside an editable section: kept exactly as it is. */
const LOCKED = '[data-dv-stash], .tc-figure, blockquote, .tc-nap, [itemscope], [data-tiq]';
/* The FAQ is schema.org markup all the way down (FAQPage > Question > Answer),
   so its itemscopes are what gets edited there, not islands. */
const FAQ_LOCKED = '[data-dv-stash], .tc-figure, [data-tiq]';
/* One element each, which Enter or a heading/list would split or wrap: a facts
   table cell, an FAQ question or answer (each is one schema property). Text,
   bold, italic and links only. */
const LINES = 'td, th, .tc-faq, [data-dv-quote]';
/* A review quote is schema.org Review markup: its words are editable, the
   reviewer, the company and the markup around them are not. */
const QUOTE = 'blockquote [itemprop="reviewBody"]';
/* The photos withCaseStudyPhotos keeps in sync. Their HTML is not the
   draft's: it is stashed and put back untouched, and the box shows either this
   label or, when the caller gives one (`slot`), the photo rows themselves,
   rendered by React into the box. Either way, nothing to click through to. */
const SLOTS: [string, string][] = [
  ['.tc-gallery[data-tiq="gallery"]', 'Gallery goes here'], ['.tc-figure[data-tiq="figure"]', 'Photo goes here'],
  // An Astro block the editor can't draw (astro-case-study.ts); it names itself.
  ['[data-tiq="block"]', 'Block'],
];
const BLOCKS = new Set(['P', 'H2', 'H3', 'H4', 'LI']);
/* Where a review quote can go when it is moved past the edge of its section:
   the story's prose, not the takeaways, the facts table, the CTA or the FAQ. */
const PROSE = '.tc-lede, .tc-context, .tc-content';

const GRIP = '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><g fill="currentColor"><circle cx="4.5" cy="3" r="1.3"/><circle cx="9.5" cy="3" r="1.3"/><circle cx="4.5" cy="7" r="1.3"/><circle cx="9.5" cy="7" r="1.3"/><circle cx="4.5" cy="11" r="1.3"/><circle cx="9.5" cy="11" r="1.3"/></g></svg>';

/* The drag handles are plain DOM inside the article (it is not React's to
   render), islands like the rest of the structure, and dropped in clean().
   They show on hover only, so the draft still reads as the article. */
type HandleKind = 'section' | 'quote' | 'slot';
const DRAG_TITLE: Record<HandleKind, string> = {
  section: 'Drag to move this section', quote: 'Drag to move this quote', slot: 'Drag to move the photos',
};

function handle(doc: Document, kind: HandleKind): HTMLElement {
  const bar = doc.createElement('div');
  bar.className = `cq-article__grab cq-article__grab--${kind}`;
  bar.setAttribute('contenteditable', 'false');
  bar.setAttribute('data-dv-ui', kind);
  bar.setAttribute('data-dv-lock', '');
  const grip = doc.createElement('button');
  grip.type = 'button';
  grip.setAttribute('data-dv-act', 'drag');
  grip.title = DRAG_TITLE[kind];
  grip.innerHTML = GRIP;
  bar.append(grip);
  if (kind === 'quote') {
    const rm = doc.createElement('button');
    rm.type = 'button';
    rm.setAttribute('data-dv-act', 'remove');
    rm.title = 'Remove this quote';
    rm.textContent = 'Remove';
    bar.append(rm);
  }
  return bar;
}

const isUi = (el: Element | null) => !!el?.matches('[data-dv-ui]');

/** A place a dragged block can land: before `before` in `parent` (null: at the end). */
interface Gap { parent: Element; before: Element | null; y: number; left: number; width: number }

/* Sections land between the article's top-level sections. A quote or the
   photos land between the blocks of the story's prose sections, not in the
   takeaways, the facts table, the CTA or the FAQ. Measured on every move, so scrolling while
   dragging can't leave the targets stale. */
function gaps(root: Element, unit: Element, kind: string): Gap[] {
  const parents = kind === 'section' ? [root] : [...root.querySelectorAll(PROSE)].filter((el) => el.matches('[data-dv-spot]'));
  const out: Gap[] = [];
  for (const parent of parents) {
    const r = parent.getBoundingClientRect();
    const kids = [...parent.children].filter((k) => !isUi(k) && k !== unit);
    let prevBottom: number | null = null;
    for (const k of kids) {
      const kr = k.getBoundingClientRect();
      out.push({ parent, before: k, y: prevBottom === null ? kr.top : (prevBottom + kr.top) / 2, left: r.left, width: r.width });
      prevBottom = kr.bottom;
    }
    out.push({ parent, before: null, y: prevBottom ?? r.top, left: r.left, width: r.width });
  }
  return out;
}

export interface DraftEditorHandle {
  /** The edited HTML, cleaned of everything the editor added. */
  getHtml(): string;
  /** Whether anything was typed or formatted since it opened. */
  isDirty(): boolean;
}

/* Our own generator's output, but model-written all the same: nothing in it
   gets to run in the app. */
function sanitize(root: ParentNode) {
  root.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((n) => n.remove());
  root.querySelectorAll('*').forEach((el) => {
    for (const a of [...el.attributes]) {
      if (/^on/i.test(a.name) || (/^(href|src|data-src)$/i.test(a.name) && /^\s*javascript:/i.test(a.value))) el.removeAttribute(a.name);
    }
  });
}

/* The grid's photos are lazy-loaded on the client's site (data-src, no src);
   here they are shown at once, and the src is taken off again on save. */
function showMedia(root: ParentNode) {
  root.querySelectorAll<HTMLElement>('img[data-src], video[data-src]').forEach((el) => {
    if (el.hasAttribute('src')) return;
    el.setAttribute('src', el.getAttribute('data-src') || '');
    el.setAttribute('data-dv-src', '');
  });
}

/* Swap each photo block for its placeholder, keeping the block's HTML to put
   back on save. */
function stashSlots(doc: Document): string[] {
  const stash: string[] = [];
  for (const [sel, label] of SLOTS) {
    doc.body.querySelectorAll(sel).forEach((el) => {
      const box = doc.createElement('div');
      box.className = 'cq-article__slot';
      box.setAttribute('data-dv-stash', String(stash.length));
      const text = doc.createElement('span');
      text.className = 'cq-article__slotlabel';
      text.textContent = el.getAttribute('data-label') || label;
      const body = doc.createElement('div');
      body.className = 'cq-article__slotbody';
      box.append(text, body);
      stash.push(el.outerHTML);
      el.replaceWith(box);
    });
  }
  return stash;
}

function build(html: string, editing: boolean): { frag: DocumentFragment; stash: string[] } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitize(doc.body);
  const stash = stashSlots(doc);
  showMedia(doc.body);
  if (editing) {
    let spots = [...doc.body.querySelectorAll<HTMLElement>(SPOTS)];
    // A client's own layout without the house classes: all of it is prose.
    if (!spots.length) {
      const wrap = doc.createElement('div');
      wrap.setAttribute('data-dv-wrap', '');
      wrap.append(...doc.body.childNodes);
      doc.body.append(wrap);
      spots = [wrap];
    }
    for (const s of spots) {
      s.setAttribute('contenteditable', 'true');
      s.setAttribute('data-dv-spot', '');
      s.querySelectorAll<HTMLElement>(s.matches('.tc-faq') ? FAQ_LOCKED : LOCKED).forEach((l) => {
        l.setAttribute('contenteditable', 'false');
        l.setAttribute('data-dv-lock', '');
      });
      // An editing host of its own inside the locked quote.
      s.querySelectorAll<HTMLElement>(QUOTE).forEach((q) => {
        q.setAttribute('contenteditable', 'true');
        q.setAttribute('data-dv-quote', '');
      });
      if (!s.matches('.tc-faq')) s.querySelectorAll('blockquote').forEach((b) => b.prepend(handle(doc, 'quote')));
      /* Where the photos go: the gallery or one-photo figure, or with none yet,
         the template's empty figure (the next sync fills the first figure). */
      if (s.matches(PROSE)) s.querySelectorAll(':scope > [data-dv-stash], :scope > .tc-figure').forEach((b) => b.prepend(handle(doc, 'slot')));
    }
    // The template's sections, top level: each can be dragged up and down the article.
    const sections = [...doc.body.children];
    if (sections.length > 1) sections.forEach((sec) => { if (/^(DIV|SECTION|ASIDE)$/.test(sec.tagName)) sec.prepend(handle(doc, 'section')); });
  }
  const frag = document.createDocumentFragment();
  frag.append(...doc.body.childNodes);
  return { frag, stash };
}

const unwrap = (el: Element) => el.replaceWith(...el.childNodes);

function rename(el: Element, tag: string) {
  const next = el.ownerDocument.createElement(tag);
  for (const a of [...el.attributes]) next.setAttribute(a.name, a.value);
  next.append(...el.childNodes);
  el.replaceWith(next);
}

/* What the browser leaves behind when people type: <b>/<i>, style attributes
   and bare spans from merging lines, empty paragraphs, a trailing <br>, a <div>
   where Enter split a line. Cleaned inside the prose sections only — the
   islands and the rest of the template are exactly as they arrived. */
function clean(root: HTMLElement, stash: readonly string[]): string {
  const c = root.cloneNode(true) as HTMLElement;
  c.querySelectorAll('[data-dv-ui]').forEach((el) => el.remove());
  c.querySelectorAll('[data-dv-dragging]').forEach((el) => el.removeAttribute('data-dv-dragging'));
  c.querySelectorAll('[data-dv-src]').forEach((el) => { el.removeAttribute('src'); el.removeAttribute('data-dv-src'); });
  for (const spot of c.querySelectorAll<HTMLElement>('[data-dv-spot]')) {
    // What was typed in: outside the islands, or in the one island that edits (the quote's words).
    const prose = (sel: string) => [...spot.querySelectorAll(sel)].filter((el) => !el.closest('[data-dv-lock]') || el.closest('[data-dv-quote]'));
    prose('b').forEach((el) => rename(el, 'strong'));
    prose('i').forEach((el) => rename(el, 'em'));
    prose('[style]').forEach((el) => el.removeAttribute('style'));
    prose('span, font').forEach((el) => { if (!el.attributes.length || el.tagName === 'FONT') unwrap(el); });
    // Enter in a section can make a <div>; a bare one is a paragraph.
    [...spot.children].forEach((el) => { if (el.tagName === 'DIV' && !el.attributes.length) rename(el, 'p'); });
    prose('br').forEach((br) => {
      const p = br.parentElement;
      if (p && BLOCKS.has(p.tagName) && !br.nextSibling) br.remove();
    });
    // An emptied block goes, unless it carries markup (an FAQ answer's itemprop).
    prose('p, h2, h3, h4, li').forEach((el) => {
      if (!el.attributes.length && !el.textContent?.replace(/ /g, ' ').trim() && !el.querySelector('img, video')) el.remove();
    });
    prose('ul, ol').forEach((el) => { if (!el.querySelector('li')) el.remove(); });
    // A list started inside a paragraph lands in it; a <ul> in a <p> is invalid HTML.
    // Split the paragraph around it, so text on either side keeps its place.
    prose('p > ul, p > ol').forEach((list) => {
      const p = list.parentElement!;
      const after = p.cloneNode(false) as HTMLElement;
      while (list.nextSibling) after.append(list.nextSibling);
      p.after(list);
      list.after(after);
      for (const el of [p, after]) if (!el.textContent?.trim() && !el.querySelector('img, video')) el.remove();
    });
  }
  c.querySelectorAll('[data-dv-lock]').forEach((el) => { el.removeAttribute('contenteditable'); el.removeAttribute('data-dv-lock'); });
  c.querySelectorAll('[data-dv-quote]').forEach((el) => { el.removeAttribute('contenteditable'); el.removeAttribute('data-dv-quote'); });
  c.querySelectorAll('[data-dv-spot]').forEach((el) => { el.removeAttribute('contenteditable'); el.removeAttribute('data-dv-spot'); });
  c.querySelectorAll('[data-dv-wrap]').forEach(unwrap);
  c.querySelectorAll('[data-dv-stash]').forEach((el) => { el.outerHTML = stash[Number(el.getAttribute('data-dv-stash'))] ?? ''; });
  /* The browser writes itemscope as itemscope="" and drops the slash from
     <img />; put both back so saving changes only what was edited. The
     parser's added <tbody> stays: every browser adds it to the page anyway. */
  return c.innerHTML
    .replace(/\bitemscope=""/g, 'itemscope')
    .replace(/<(img|br|hr|source)\b([^>]*?)\s*\/?>/gi, '<$1$2 />');
}

/** The block the caret is in, for the toolbar's state: p, h2, h3, ul, ol. */
function blockAt(node: Node | null, spot: Element): string {
  for (let n: Node | null = node; n && n !== spot; n = n.parentNode) {
    if (!(n instanceof HTMLElement)) continue;
    if (n.tagName === 'LI') return n.parentElement?.tagName === 'OL' ? 'ol' : 'ul';
    if (/^(P|H2|H3|H4)$/.test(n.tagName)) return n.tagName.toLowerCase();
  }
  return '';
}

export const HtmlDraft = forwardRef<DraftEditorHandle, {
  html: string; editing: boolean;
  /** What to show in the photo placeholders in place of their label. */
  slot?: () => ReactNode;
}>(function HtmlDraft({ html, editing, slot }, ref) {
  const root = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dirty = useRef(false);
  const saved = useRef<Range | null>(null);
  const stash = useRef<string[]>([]);
  const [state, setState] = useState<{ inSpot: boolean; line: boolean; block: string; bold: boolean; italic: boolean }>({ inSpot: false, line: false, block: '', bold: false, italic: false });
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState('');
  const [linkErr, setLinkErr] = useState('');
  // The quote waiting on "Remove it?": its schema markup is the page's Review, gone for good once saved.
  const [removing, setRemoving] = useState<Element | null>(null);
  // The placeholders' insides, rebuilt with the article; the build count keys the portals.
  const [slots, setSlots] = useState<{ n: number; els: Element[] }>({ n: 0, els: [] });

  // Rebuilt only when the draft or the mode changes, never while typing: that
  // would throw the caret back to the start.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const built = build(html, editing);
    stash.current = built.stash;
    el.replaceChildren(built.frag);
    const els = slot ? [...el.querySelectorAll('[data-dv-stash]')] : [];
    els.forEach((b) => b.classList.add('is-live'));
    setSlots((prev) => ({ n: prev.n + 1, els: els.map((b) => b.querySelector('.cq-article__slotbody')!) }));
    dirty.current = false;
  }, [html, editing]);

  useImperativeHandle(ref, () => ({
    getHtml: () => (root.current ? clean(root.current, stash.current) : html),
    isDirty: () => dirty.current,
  }), [html]);

  const spotOf = (n: Node | null) => (n instanceof Element ? n : n?.parentElement)?.closest('[data-dv-spot]') ?? null;
  const caretSpot = () => {
    const sel = window.getSelection();
    const s = sel?.rangeCount ? spotOf(sel.anchorNode) : null;
    return s && root.current?.contains(s) ? s : null;
  };

  useEffect(() => {
    if (!editing) return;
    const update = () => {
      const s = caretSpot();
      const sel = window.getSelection();
      const block = s ? blockAt(sel!.anchorNode, s) : '';
      setState(s ? {
        inSpot: true, line: inLine(sel!.anchorNode), block,
        // A heading is bold by its own style; B lit up there would claim it was applied.
        bold: !/^h\d$/.test(block) && document.queryCommandState('bold'), italic: document.queryCommandState('italic'),
      } : (prev) => (prev.inSpot ? { inSpot: false, line: false, block: '', bold: false, italic: false } : prev));
    };
    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, [editing]);

  const inLine = (n: Node | null) => !!(n instanceof Element ? n : n?.parentElement)?.closest(LINES);
  const run = (cmd: string, arg?: string) => {
    if (!caretSpot()) return;
    document.execCommand(cmd, false, arg);
    dirty.current = true;
  };
  // The same button again turns a heading back into a paragraph, a list back into lines.
  const block = (tag: 'p' | 'h2' | 'h3') => run('formatBlock', state.block === tag ? 'p' : tag);
  const list = (kind: 'ul' | 'ol') => run(kind === 'ul' ? 'insertUnorderedList' : 'insertOrderedList');

  const startLink = () => {
    const sel = window.getSelection();
    if (!caretSpot() || !sel) return;
    saved.current = sel.getRangeAt(0).cloneRange();
    setUrl(''); setLinkErr(''); setLinking(true);
  };
  const applyLink = () => {
    const href = normalizeLink(url) ?? (/^\/[^\s]*$/.test(url.trim()) ? url.trim() : null);
    if (!href) { setLinkErr('Enter a web address, a page path like /services, or an email.'); return; }
    const sel = window.getSelection();
    if (sel && saved.current) { sel.removeAllRanges(); sel.addRange(saved.current); }
    const s = caretSpot();
    if (s) {
      (s as HTMLElement).focus();
      if (sel && !sel.isCollapsed) document.execCommand('createLink', false, href);
      else document.execCommand('insertHTML', false, `<a href="${href.replace(/"/g, '&quot;')}">${href.replace(/^mailto:/i, '').replace(/</g, '&lt;')}</a>&nbsp;`);
      dirty.current = true;
    }
    setLinking(false);
  };

  /* An island can't be typed into (contenteditable=false), but a selection
     running across one would still delete it whole. */
  const guardDelete = (e: InputEvent) => {
    if (!e.inputType.startsWith('delete') && e.inputType !== 'insertText' && e.inputType !== 'insertParagraph') return;
    const ranges = e.getTargetRanges?.() ?? [];
    const locked = root.current?.querySelectorAll('[data-dv-lock]') ?? [];
    for (const r of ranges) {
      const live = document.createRange();
      live.setStart(r.startContainer, r.startOffset); live.setEnd(r.endContainer, r.endOffset);
      if ([...locked].some((l) => live.intersectsNode(l) && !l.contains(live.startContainer))) { e.preventDefault(); return; }
    }
  };
  useEffect(() => {
    const el = root.current;
    if (!el || !editing) return;
    const onBefore = (e: Event) => guardDelete(e as InputEvent);
    el.addEventListener('beforeinput', onBefore);
    return () => el.removeEventListener('beforeinput', onBefore);
  }, [editing]);

  /* Drags and removes are DOM moves, not execCommand, so they are not on the
     browser's undo stack; Cancel on the draft is the way back. Pointer events,
     not HTML5 drag and drop: a native drop into a contentEditable section
     would also paste the dragged HTML in as text. */
  const startDrag = (grip: Element, e: ReactPointerEvent) => {
    const el = root.current, wrap = wrapRef.current;
    const bar = grip.closest('[data-dv-ui]');
    const unit = bar?.parentElement;
    if (!el || !wrap || !bar || !unit) return;
    e.preventDefault();
    const kind = bar.getAttribute('data-dv-ui')!;
    const line = document.createElement('div');
    line.className = 'cq-article__drop';
    wrap.append(line);
    unit.setAttribute('data-dv-dragging', '');
    document.body.classList.add('is-dragging-block');
    let at: Gap | null = null;
    let lastY = e.clientY;
    const place = (y: number) => {
      lastY = y;
      at = null;
      for (const g of gaps(el, unit, kind)) if (!at || Math.abs(g.y - y) < Math.abs(at.y - y)) at = g;
      if (!at) { line.style.display = 'none'; return; }
      const w = wrap.getBoundingClientRect();
      line.style.display = '';
      line.style.top = `${at.y - w.top}px`;
      line.style.left = `${at.left - w.left}px`;
      line.style.width = `${at.width}px`;
    };
    // Held near the top or bottom of the window: keep scrolling, so a section can travel the whole article.
    const scroller = window.setInterval(() => {
      const edge = 70;
      const dy = lastY < edge ? -14 : lastY > window.innerHeight - edge ? 14 : 0;
      if (dy) { window.scrollBy(0, dy); place(lastY); }
    }, 16);
    const move = (ev: PointerEvent) => place(ev.clientY);
    const end = (ev: Event) => {
      window.clearInterval(scroller);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('keydown', esc);
      line.remove();
      unit.removeAttribute('data-dv-dragging');
      document.body.classList.remove('is-dragging-block');
      const g = at as Gap | null;
      if (ev.type !== 'pointerup' || !g) return;
      const before = g.before;
      // Dropped where it already was: no change, not dirty.
      if (g.parent === unit.parentElement && (before === unit.nextElementSibling || before === unit)) return;
      g.parent.insertBefore(unit, before);
      dirty.current = true;
    };
    const esc = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { at = null; end(ev); } };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('keydown', esc);
    place(e.clientY);
  };

  // Headings and lists: not in a table cell or the FAQ (see LINES).
  const btn = (label: ReactNode, title: string, on: boolean, act: () => void, structural = false) => (
    <button type="button" className={`rte__btn${on ? ' is-on' : ''}`} title={title} aria-pressed={on}
      disabled={!state.inSpot || (structural && state.line)} onMouseDown={(e) => { e.preventDefault(); act(); }}>
      {label}
    </button>
  );

  return (
    <div ref={wrapRef} className={`cq-article-wrap${editing ? ' is-editing' : ''}`}>
      {editing && (
        <div className="cq-article__bar">
          <div className="rte__bar" role="toolbar" aria-label="Draft formatting">
            {btn('Text', 'Paragraph', state.block === 'p', () => block('p'), true)}
            {btn('H2', 'Heading', state.block === 'h2', () => block('h2'), true)}
            {btn('H3', 'Subheading', state.block === 'h3', () => block('h3'), true)}
            <span className="rte__sep" />
            {btn(<Icon name="list" size={18} />, 'Bulleted list', state.block === 'ul', () => list('ul'), true)}
            {btn(<span className="rte__ol">1.</span>, 'Numbered list', state.block === 'ol', () => list('ol'), true)}
            <span className="rte__sep" />
            {btn(<strong>B</strong>, 'Bold (Ctrl+B)', state.bold, () => run('bold'))}
            {btn(<em>I</em>, 'Italic (Ctrl+I)', state.italic, () => run('italic'))}
            {btn(<Icon name="link" size={18} />, 'Add link', false, startLink)}
            {btn(<><Icon name="link" size={18} /><Icon name="x" size={13} /></>, 'Remove link', false, () => run('unlink'))}
            <span className="cq-article__hint">{state.inSpot ? '' : 'Click into a highlighted section to edit it'}</span>
          </div>
          {linking && (
            <div className="rte__link">
              <input className="tr-input" autoFocus placeholder="https://…, /a-page-on-this-site or name@example.com" value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                  if (e.key === 'Escape') { e.preventDefault(); setLinking(false); }
                }} />
              <button type="button" className="rte__btn rte__btn--text" onClick={applyLink}>Add</button>
              <button type="button" className="rte__btn rte__btn--text" onClick={() => setLinking(false)}>Cancel</button>
              {linkErr && <span className="rte__err">{linkErr}</span>}
            </div>
          )}
        </div>
      )}
      <div ref={root} className="cq-article"
        onFocus={() => { if (editing) document.execCommand('defaultParagraphSeparator', false, 'p'); }}
        onInput={() => { dirty.current = true; }}
        onKeyDown={(e) => {
          // A new line would split a table cell or a schema property in two.
          if (editing && e.key === 'Enter' && inLine(window.getSelection()?.anchorNode ?? null)) e.preventDefault();
        }}
        onPaste={(e) => {
          if (!editing) return;
          e.preventDefault();
          if (caretSpot()) { document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); dirty.current = true; }
        }}
        /* A link in the draft is the client's site (root-relative paths are
           theirs, not ours): following it here would leave the page, and any
           unsaved edits with it. */
        onClick={(e) => {
          if ((e.target as Element).closest('a')) e.preventDefault();
          const rm = (e.target as Element).closest('[data-dv-act="remove"]');
          const unit = rm?.closest('[data-dv-ui]')?.parentElement;
          if (unit) setRemoving(unit);
        }}
        onPointerDown={(e) => {
          const grip = (e.target as Element).closest('[data-dv-act="drag"]');
          if (editing && grip && e.button === 0) startDrag(grip, e);
        }}
        // Pressing a handle shouldn't take the caret out of what is being typed.
        onMouseDown={(e) => { if ((e.target as Element).closest('[data-dv-act]')) e.preventDefault(); }} />
      {slot && slots.els.map((el, i) => createPortal(slot(), el, `${slots.n}-${i}`))}
      {removing && (
        <Modal title="Remove this quote?" onClose={() => setRemoving(null)}>
          <div className="es-modal__body">
            <p className="es-hint" style={{ margin: 0 }}>The customer quote and its review markup come out of the draft. Cancel on the draft brings it back until you save.</p>
          </div>
          <div className="es-modal__foot">
            <Button variant="ghost" size="sm" onClick={() => setRemoving(null)}>Keep it</Button>
            <Button variant="danger" size="sm" autoFocus onClick={() => { removing.remove(); dirty.current = true; setRemoving(null); }}>Remove quote</Button>
          </div>
        </Modal>
      )}
    </div>
  );
});
